import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { checkBooking, unbookPlace, unbookRoomPlace } from 'src/api/bookings';
import {
  findBookings,
  FindBookingsParams,
  FindBookingsResponse,
  BookingInfo,
} from 'src/api/bookingsView';
import { SortDirection } from 'src/api/types';
import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import { PlaceType, RoomType } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';

import { messagesActions } from '../messages/slice';
import { StoreState, ThunkConfig } from '../types';
import { getEmployeeNumber } from '../user/selectors';

import { supportsAuthor } from './consts';
import { getBookingsState } from './selectors';
import { BookingsPageMode, BookingsPageRole } from './types';

const defaultLimit = 15;

const bookingObjectTypeToFindBookingsParams = (
  bookingObjectType: BookingObjectType,
): Partial<FindBookingsParams> => {
  const paramsByBookingObjectType: Record<
    BookingObjectType,
    Partial<FindBookingsParams>
  > = {
    [BookingObjectType.Desk]: { filterPlaceType: PlaceType.Desk },
    [BookingObjectType.FocusRoom]: {
      filterPlaceType: PlaceType.Room,
      filterRoomType: RoomType.Focus,
    },
    [BookingObjectType.MeetingRoom]: {
      filterPlaceType: PlaceType.Room,
      filterRoomType: RoomType.Meeting,
    },
    [BookingObjectType.PhoneRoom]: {
      filterPlaceType: PlaceType.Room,
      filterRoomType: RoomType.Phone,
    },
    [BookingObjectType.Parking]: { filterPlaceType: PlaceType.Parking },
  };

  return paramsByBookingObjectType[bookingObjectType];
};

const roleToFindBookingsParams = (
  employeeNumber: string | undefined,
  bookingObjectType: BookingObjectType,
  role: BookingsPageRole = BookingsPageRole.Author,
): Partial<FindBookingsParams> => {
  if (!supportsAuthor.has(bookingObjectType))
    return {
      filterEmployeeNumber: employeeNumber,
    };

  const paramsByRole: Record<BookingsPageRole, Partial<FindBookingsParams>> = {
    [BookingsPageRole.Author]: { filterAuthorEmployeeNumber: employeeNumber },
    [BookingsPageRole.Responsible]: {
      filterEmployeeNumber: employeeNumber,
    },
  };

  return paramsByRole[role];
};

const actualToFindBookingsParams = (
  actual: boolean,
  date: Date,
): Partial<FindBookingsParams> => {
  const dateString = date.toISOString();
  return actual
    ? {
        bookedToAfter: dateString,
        sortDirection: SortDirection.Asc,
        sortField: 'dateBookingFrom',
      }
    : {
        bookedToBefore: dateString,
        sortDirection: SortDirection.Desc,
        sortField: 'dateBookingFrom',
      };
};

const datesToFindBookingsParams = (
  from: string | undefined,
  to: string | undefined,
): Partial<FindBookingsParams> => {
  if (!from || !to) return {};

  const fromDate = getDateTime({
    date: new Date(from),
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const toDate = getDateTime({
    date: new Date(to),
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  toDate.setDate(toDate.getDate() + 1);

  return {
    searchFrom: fromDate.toISOString(),
    searchTo: toDate.toISOString(),
  };
};

export interface FetchBookingsReturn extends FindBookingsResponse {
  now: Date;
}

export const fetchBookings = createAsyncThunk<
  FetchBookingsReturn | void,
  boolean,
  ThunkConfig<string>
>(
  'bookings/fetchBookings',
  async (fromStart, { dispatch, getState, rejectWithValue, requestId }) => {
    try {
      const state = getState() as StoreState;
      const myEmployeeNumber = getEmployeeNumber(state);
      const {
        bookingObjectType,
        mode,
        from,
        to,
        token,
        hasMore: hasMoreFromState,
        now: nowFromState,
        currentRequestId,
        employee,
        role,
      } = getBookingsState(state);

      const now = fromStart ? new Date() : nowFromState;
      const hasMore = fromStart ? true : hasMoreFromState;

      if (
        !hasMore ||
        !now ||
        currentRequestId !== requestId ||
        !bookingObjectType
      )
        return;

      const actual = mode === BookingsPageMode.Actual;

      const params: FindBookingsParams = {
        limit: defaultLimit,
        deleted: false,
        ...roleToFindBookingsParams(
          (employee?.id as string | undefined) ?? myEmployeeNumber,
          bookingObjectType,
          role,
        ),
        ...bookingObjectTypeToFindBookingsParams(bookingObjectType),
        ...actualToFindBookingsParams(actual, now),
        ...datesToFindBookingsParams(from, to),
      };

      if (fromStart) {
        params.offset = 0;
      } else if (token) {
        params.from = token;
      }

      const response = await findBookings(params);

      return { ...response, now };
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (fromStart, { getState }) => {
      const state = getState() as StoreState;
      const { mode, bookingObjectType } = getBookingsState(state);

      if (!mode || !bookingObjectType) return false;
    },
  },
);

export const unbookBooking = createAsyncThunk<
  void,
  BookingInfo,
  ThunkConfig<string>
>('bookings/unbookBooking', async (booking, { dispatch, rejectWithValue }) => {
  try {
    const unbookPlaceResponse =
      booking.placeType === PlaceType.Room
        ? await unbookRoomPlace({
            bookingId: booking.bookingId,
            placeId: booking.placeId,
            dateFrom: booking.bookedFrom,
            dateTo: booking.bookedTo,
          })
        : await unbookPlace(booking.bookingId);

    if (!unbookPlaceResponse.accepted)
      throw new Error(unbookPlaceResponse.rejectedReason ?? defaultServerError);

    const checkBookPlaceResponse = await checkBooking({
      commandId: unbookPlaceResponse.commandId,
      maxDurationExceededError:
        'Заявка на отмену брони отправлена. Отмену брони можешь проверить в своём календаре в Outlook',
    });

    if (checkBookPlaceResponse.processed) {
      dispatch(fetchBookings(true));
    }
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});
